import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import {
  FileText, Upload, Trash2, Download, RotateCcw, MousePointerClick,
  FileCheck, PenSquare, ShieldCheck,
} from "lucide-react";
import FormFields from "./FormFields";
import { FillForm, AuditRecord } from "./AdminFormFill";

/* The agent's form library, and the per-client switches that decide which of
   its forms a given seller actually sees.

   Both live here rather than inside their host pages because they talk to the
   same endpoints and share the same vocabulary. The library saves on every
   action instead of into the Content page's draft -- a form is a file, not a
   paragraph of copy, and pretending it needs a Save button would be a lie.
   TimezoneCard on the same page already works this way. */

const SIDE_LABEL = { sell: "Sellers", buy: "Buyers", both: "Both" };

function sidesKey(sides) {
  const s = sides || ["sell"];
  return s.length > 1 ? "both" : (s[0] || "sell");
}

async function openBlob(url, filename) {
  const { data } = await api.get(url, { responseType: "blob" });
  const href = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename || "form.pdf";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the click a moment to start before the URL stops resolving.
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

const muted = { color: "var(--text-muted)" };
const rowStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(0,1fr) auto",
  gap: 12,
  alignItems: "center",
  padding: "14px 18px",
  borderTop: "1px solid var(--border)",
};

/* Where one form stands for one client. `rank` drives the sort, so whatever
   is still outstanding floats to the top of the page instead of sitting
   wherever the agent's library happens to order it. */
const STATES = {
  you:      { key: "you",      rank: 0, tone: "warn",  label: "Waiting on you" },
  them:     { key: "them",     rank: 1, tone: "warn",  label: "Waiting on them" },
  nofields: { key: "nofields", rank: 2, tone: "muted", label: "No fields placed" },
  signed:   { key: "signed",   rank: 3, tone: "good",  label: "Signed" },
  off:      { key: "off",      rank: 4, tone: "muted", label: "Not sent" },
};

const TONE = {
  good:  { bg: "rgba(43,76,63,0.10)",  fg: "var(--primary)" },
  warn:  { bg: "rgba(184,92,71,0.12)", fg: "var(--secondary)" },
  muted: { bg: "var(--surface-alt)",   fg: "var(--text-muted)" },
};

/* An action that says what it does. The row used to carry four bare icons,
   which meant hovering to find out -- and a phone has no hover. */
function Act({ testid, icon: Icon, label, onClick }) {
  return (
    <button type="button" className="btn-ghost" data-testid={testid} onClick={onClick}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12,
                     padding: "5px 9px", borderRadius: 7,
                     border: "1px solid var(--border)" }}>
      <Icon size={13} /> {label}
    </button>
  );
}

/* ---------------------------------------------------------- the library */

export function FormLibraryEditor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  // Which form's fields are being placed, if any. A modal rather than its
  // own route: the agent is partway down a list and wants to land back on it.
  const [editing, setEditing] = useState(null);

  const load = async (archived = showArchived) => {
    try {
      const { data } = await api.get(`/admin/form-library?include_archived=${archived}`);
      setRows(data.templates || []);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't load your forms.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 3000); };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setErr("");
    if (file.type !== "application/pdf") { setErr("PDFs only for now."); return; }
    if (file.size > 25 * 1024 * 1024) { setErr("That file is over 25 MB."); return; }
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", file.name.replace(/\.pdf$/i, ""));
      await api.post("/admin/form-library", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      await load();
      flash("Added — it's on for everyone, so it's already on your clients' Forms pages. Untick Everyone to choose per client.");
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail) || "Upload failed.");
    } finally { setBusy(""); }
  };

  const patch = async (t, body) => {
    setBusy(t.template_id);
    setErr("");
    // Optimistic: these are one-click switches and a round-trip of lag makes
    // them feel broken. Reload after, so the server still has the last word.
    setRows((xs) => xs.map((x) => (x.template_id === t.template_id ? { ...x, ...body } : x)));
    try {
      await api.patch(`/admin/form-library/${t.template_id}`, body);
      await load();
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't save that.");
      await load();
    } finally { setBusy(""); }
  };

  const archive = async (t) => {
    if (!window.confirm(
      `Archive "${t.name}"?\n\nIt stops appearing for every client, but anything already completed on it stays intact. You can bring it back later.`
    )) return;
    setBusy(t.template_id);
    try {
      await api.delete(`/admin/form-library/${t.template_id}`);
      await load();
      flash("Archived.");
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't archive that.");
    } finally { setBusy(""); }
  };

  return (
    <div className="card" style={{ padding: 28, marginBottom: 20 }} data-testid="form-library">
      <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: 0 }}>Your forms</h2>
      <p style={{ ...muted, fontSize: 14, margin: "4px 0 18px", maxWidth: 640 }}>
        The blank forms your state and brokerage need. Whatever you add here shows up on your
        client's <b style={{ color: "var(--text)" }}>Forms</b> page, ready to download — for
        everyone, or only the clients you switch it on for.
      </p>

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
        <input ref={fileRef} type="file" accept="application/pdf" onChange={upload}
               style={{ display: "none" }} data-testid="form-library-file" />
        <button type="button" className="btn-secondary" data-testid="form-library-add"
                disabled={busy === "upload"}
                onClick={() => fileRef.current && fileRef.current.click()}>
          <Upload size={14} /> {busy === "upload" ? "Uploading…" : "Add a form"}
        </button>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13.5, cursor: "pointer", ...muted }}>
          <input type="checkbox" checked={showArchived} data-testid="form-library-show-archived"
                 onChange={(e) => { setLoading(true); setShowArchived(e.target.checked); }} />
          Show archived
        </label>
        {msg && <span style={{ fontSize: 13.5, color: "var(--primary)" }}>{msg}</span>}
        {err && <span style={{ fontSize: 13.5, color: "var(--secondary)" }}>{err}</span>}
      </div>

      <div style={{ border: "1px solid var(--border)", borderRadius: 10, marginTop: 16, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 22, ...muted }}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 22, ...muted }} data-testid="form-library-empty">
            Nothing here yet. Add the blank forms you hand to every seller — disclosures,
            data forms, your own intake sheets.
          </div>
        ) : rows.map((t) => (
          <div key={t.template_id} style={{ ...rowStyle, opacity: t.archived ? 0.55 : 1 }}
               data-testid={`form-row-${t.template_id}`}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <FileText size={14} style={{ flex: "0 0 14px", ...muted }} />
                <input
                  className="input-field"
                  data-testid={`form-name-${t.template_id}`}
                  defaultValue={t.name}
                  disabled={t.archived}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && v !== t.name) patch(t, { name: v });
                    else e.target.value = t.name;
                  }}
                  style={{ fontWeight: 500, width: "100%", maxWidth: 420, padding: "6px 10px", fontSize: 14 }}
                />
                {t.archived && (
                  <span className="badge" style={{ background: "var(--surface-alt)", ...muted }}>Archived</span>
                )}
              </div>
              <div style={{ ...muted, fontSize: 12.5, marginTop: 6, paddingLeft: 22 }}>
                {t.page_count ? `${t.page_count} page${t.page_count === 1 ? "" : "s"} · ` : ""}
                {Math.round((t.size || 0) / 1024)} KB
                {t.completed_by === "agent"
                  ? " · only you see this one"
                  : " · appears on your client's Forms page"}
                {t.field_count > 0
                  ? ` · ${t.field_count} field${t.field_count === 1 ? "" : "s"} placed`
                  : ""}
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
              {!t.archived && (
                <>
                  <select className="input-field" style={{ padding: "6px 8px", fontSize: 12.5, maxWidth: 150 }}
                          value={t.completed_by} disabled={busy === t.template_id}
                          data-testid={`form-who-${t.template_id}`}
                          onChange={(e) => patch(t, { completed_by: e.target.value })}>
                    <option value="seller">Client fills it</option>
                    <option value="agent">I fill it</option>
                    <option value="both">We both fill it</option>
                  </select>
                  <select className="input-field" style={{ padding: "6px 8px", fontSize: 12.5, maxWidth: 120 }}
                          value={sidesKey(t.sides)} disabled={busy === t.template_id}
                          data-testid={`form-sides-${t.template_id}`}
                          onChange={(e) => patch(t, {
                            sides: e.target.value === "both" ? ["sell", "buy"] : [e.target.value],
                          })}>
                    {["sell", "buy", "both"].map((k) => (
                      <option key={k} value={k}>{SIDE_LABEL[k]}</option>
                    ))}
                  </select>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, cursor: "pointer", ...muted }}
                         title="Applies to every client unless you switch it off for one of them">
                    <input type="checkbox" checked={!!t.default_on} disabled={busy === t.template_id}
                           data-testid={`form-default-${t.template_id}`}
                           onChange={(e) => patch(t, { default_on: e.target.checked })} />
                    Everyone
                  </label>
                </>
              )}
              {!t.archived && (
                <button type="button" className="btn-ghost" style={{ padding: 6 }}
                        title="Place the fields that get filled in"
                        data-testid={`form-fields-${t.template_id}`}
                        onClick={() => setEditing(t)}>
                  <MousePointerClick size={14} />
                </button>
              )}
              <button type="button" className="btn-ghost" style={{ padding: 6 }} title="Download the blank"
                      data-testid={`form-dl-${t.template_id}`}
                      onClick={() => openBlob(`/admin/form-library/${t.template_id}/file`, t.filename)}>
                <Download size={14} />
              </button>
              {t.archived ? (
                <button type="button" className="btn-ghost" style={{ padding: 6, color: "var(--primary)" }}
                        title="Bring it back" data-testid={`form-unarchive-${t.template_id}`}
                        disabled={busy === t.template_id}
                        onClick={() => patch(t, { archived: false })}>
                  <RotateCcw size={14} />
                </button>
              ) : (
                <button type="button" className="btn-ghost" style={{ padding: 6, color: "var(--secondary)" }}
                        title="Archive" data-testid={`form-archive-${t.template_id}`}
                        disabled={busy === t.template_id}
                        onClick={() => archive(t)}>
                  <Trash2 size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <FormFields template={editing} onClose={() => setEditing(null)}
                    onSaved={() => load()} />
      )}
    </div>
  );
}

/* ------------------------------------------- which ones apply to a client */

export function ClientFormsTab({ sellerId, setToast }) {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [filling, setFilling] = useState(null);
  const [auditing, setAuditing] = useState("");
  const [signed, setSigned] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/seller/${sellerId}/form-library`);
      setForms(data.forms || []);
    } catch (e) {
      setToast && setToast(formatApiError(e?.response?.data?.detail) || "Couldn't load forms.");
    } finally { setLoading(false); }
    try {
      const { data } = await api.get(`/admin/seller/${sellerId}/signed-forms`);
      const by = {};
      // Newest first from the server, so the first one for each form wins.
      (data.signed || []).forEach((s) => { if (!by[s.template_id]) by[s.template_id] = s; });
      setSigned(by);
    } catch {
      // A client who has signed nothing is the normal case, not an error.
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sellerId]);

  const toggle = async (t) => {
    const next = forms.map((f) => (f.template_id === t.template_id ? { ...f, on: !f.on } : f));
    setForms(next);
    setBusy(t.template_id);
    try {
      const overrides = {};
      next.forEach((f) => { overrides[f.template_id] = f.on; });
      const { data } = await api.put(`/admin/seller/${sellerId}/form-library`, { overrides });
      setForms(data.forms || next);
    } catch (e) {
      setToast && setToast(formatApiError(e?.response?.data?.detail) || "Couldn't save that.");
      await load();
    } finally { setBusy(""); }
  };

  if (loading) return <div className="card" style={{ padding: 24, ...muted }}>Loading…</div>;

  if (forms.length === 0) {
    return (
      <div className="card" style={{ padding: 24, ...muted, maxWidth: 720 }} data-testid="client-forms-empty">
        No forms in your library yet. Add them under <b style={{ color: "var(--text)" }}>Content &rarr; Forms</b>,
        then choose here which ones this client needs.
      </div>
    );
  }

  /* A form switched off is not outstanding. A form switched on with no
     fields on it cannot be signed at all, which is worth saying rather than
     leaving the agent to read silence as success. */
  const stateOf = (t) => {
    if (!t.on) return STATES.off;
    if (signed[t.template_id]) return STATES.signed;
    if (t.completed_by === "agent") return STATES.you;
    if (!(t.field_count || 0)) return STATES.nofields;
    return STATES.them;
  };

  const rows = [...forms].sort((a, b) =>
    (stateOf(a).rank - stateOf(b).rank) || (a.name || "").localeCompare(b.name || ""));

  const tally = {};
  forms.forEach((t) => { const k = stateOf(t).key; tally[k] = (tally[k] || 0) + 1; });
  const live = forms.filter((t) => t.on).length;

  return (
    <div style={{ maxWidth: 720 }}>
      <div className="card" style={{ padding: 24, marginBottom: 16 }}>
        <div className="overline">Forms for this client</div>
        <div style={{ fontSize: 15, marginTop: 9 }} data-testid="client-forms-summary">
          {live === 0 ? "Nothing switched on for this client yet." : (
            <>
              <b>{tally.signed || 0} of {live}</b> signed
              {tally.you ? (
                <> &middot; <b style={{ color: "var(--secondary)" }}>{tally.you} waiting on you</b></>
              ) : null}
              {tally.them ? <> &middot; {tally.them} waiting on them</> : null}
              {tally.nofields ? <> &middot; {tally.nofields} with no fields placed</> : null}
            </>
          )}
        </div>
        <p style={{ ...muted, fontSize: 13, marginTop: 9, marginBottom: 0 }}>
          Switched on means this client sees it. Forms marked for everyone are on
          by default &mdash; turning one off here affects only this client.
        </p>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="client-forms">
        {rows.map((t) => {
          const st = stateOf(t);
          const sg = signed[t.template_id];
          const tone = TONE[st.tone] || TONE.muted;
          return (
            /* Flex rather than the shared two-column grid: at phone width the
               grid pinned the switch beside the buttons and they fought for
               the same inches. Wrapping lets the switch drop to its own line
               instead, and the chip rides with the name it describes. */
            <div key={t.template_id} data-testid={`client-form-${t.template_id}`}
                 style={{ display: "flex", flexWrap: "wrap", gap: "10px 14px",
                          alignItems: "flex-start", justifyContent: "space-between",
                          padding: "14px 18px", borderTop: "1px solid var(--border)" }}>
              <div style={{ flex: "1 1 300px", minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>{t.name}</div>
                  <span data-testid={`client-form-state-${t.template_id}`}
                        style={{ fontSize: 11.5, padding: "3px 9px", borderRadius: 999,
                                 whiteSpace: "nowrap", background: tone.bg, color: tone.fg }}>
                    {st.key === "signed" && sg
                      ? `Signed ${new Date(sg.signed_at).toLocaleDateString()}`
                      : st.label}
                  </span>
                </div>
                <div style={{ ...muted, fontSize: 12.5, marginTop: 3 }}>
                  {{ agent: "You fill this one", both: "You both fill it" }[t.completed_by]
                    || "They fill this one"}
                  {t.page_count ? ` \u00b7 ${t.page_count} page${t.page_count === 1 ? "" : "s"}` : ""}
                  {t.default_on !== t.on
                    ? ` \u00b7 ${t.on ? "switched on" : "switched off"} for this client`
                    : ""}
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 10 }}>
                  {(t.field_count || 0) > 0 && (
                    <Act testid={`client-form-fill-${t.template_id}`} icon={PenSquare}
                         label="Fill in your part" onClick={() => setFilling(t)} />
                  )}
                  {sg && (
                    <>
                      <Act testid={`client-form-signed-${t.template_id}`} icon={FileCheck}
                           label="Signed copy"
                           onClick={() => openBlob(`/signed-form/${sg.signed_form_id}/file`,
                                                   `${t.name} - signed.pdf`)} />
                      <Act testid={`client-form-audit-${t.template_id}`} icon={ShieldCheck}
                           label="Signing record"
                           onClick={() => setAuditing(sg.signed_form_id)} />
                    </>
                  )}
                  <Act testid={`client-form-blank-${t.template_id}`} icon={Download}
                       label="Blank copy"
                       onClick={() => openBlob(`/admin/form-library/${t.template_id}/file`, t.filename)} />
                </div>
              </div>

              {/* Its own target now. The whole row used to be a label
                  wrapping this box, so a mis-aimed click anywhere silently
                  changed what the client sees -- which is why every button
                  above had to call preventDefault to escape it. */}
              <label style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center",
                              gap: 7, fontSize: 12, cursor: "pointer", paddingTop: 2, ...muted }}>
                <input type="checkbox" checked={!!t.on} disabled={busy === t.template_id}
                       data-testid={`client-form-toggle-${t.template_id}`}
                       onChange={() => toggle(t)} />
                Shown
              </label>
            </div>
          );
        })}
      </div>

      {filling && (
        <FillForm sellerId={sellerId} template={filling} onClose={() => setFilling(null)} />
      )}
      {auditing && (
        <AuditRecord signedFormId={auditing} onClose={() => setAuditing("")} />
      )}
    </div>
  );
}
